'use client';

import { isNetworkConnectedAtom, pubSubService, ResponseCodeEconnabortedError, useLogger } from '@capsicum/common';
import { useAtom } from 'jotai';
import { createContext, type ReactComponentElement, useCallback, useContext, useEffect, useMemo } from 'react';

export type NetworkInfoInterface = {
	fetchNetworkInformation: () => boolean;
};
export const NetworkInfoContext = createContext<NetworkInfoInterface>({} as NetworkInfoInterface);
export const useNetInfo = () => useContext(NetworkInfoContext);

export function NetworkInfoProvider({ children }: { children: ReactComponentElement<any> }) {
	const [isNetworkConnected, setIsNetworkConnected] = useAtom(isNetworkConnectedAtom);
	const logger = useLogger();

	const fetchNetworkInformation = useCallback(() => navigator.onLine, []);

	const handleNetworkErrors = useCallback(() => {
		const isOnline = navigator.onLine;

		if (isNetworkConnected !== isOnline) {
			setIsNetworkConnected(isOnline);
			if (isOnline) {
				pubSubService.publish('resetGlobalError');
				logger.info('You are online');
			} else {
				pubSubService.publish('globalError', new ResponseCodeEconnabortedError(new Error('ERR_NETWORK')));
				logger.info('You are Offline');
			}
		}
	}, [isNetworkConnected, logger, setIsNetworkConnected]);

	useEffect(() => {
		handleNetworkErrors();
		window.addEventListener('online', handleNetworkErrors);
		window.addEventListener('offline', handleNetworkErrors);

		return () => {
			window.removeEventListener('online', handleNetworkErrors);
			window.removeEventListener('offline', handleNetworkErrors);
		};
	}, [handleNetworkErrors]);

	const value = useMemo(
		() => ({
			fetchNetworkInformation
		}),
		[fetchNetworkInformation]
	);

	return <NetworkInfoContext.Provider value={value}>{children}</NetworkInfoContext.Provider>;
}
